import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Text,
  Card,
  Grid,
  Switch,
  Box,
  Anchor,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { CodeableConceptInput, ResourceInput } from '@medplum/react';
import {
  Patient,
  Practitioner,
  Encounter,
  CodeableConcept,
} from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCalendar,
  IconClock,
  IconPlus,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@mantine/hooks';
import CreatePractitionerModal from '@/pages/ainigma/CreatePractitionerModal';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import { ARKON } from '@consts/systems.consts';
import dayjs from 'dayjs';
import { AdditionalDoctorsList } from './AdditionalDoctorsList';
import { useEncounterOperations } from '@hooks/useEncounterOperations';
import {
  useEncounterFormInitialization,
  ReasonMode,
} from '@hooks/useEncounterFormInitialization';
import { EncounterFormData } from '@/utils/encounter-builder';
import {
  BASE_ERROR_NOTIFICATION_PARAMS,
  BASE_SUCCESS_NOTIFICATION_PARAMS,
} from '@consts/notification.consts';
import DiscardChangesButton from '@/components/DiscardChangesButton';
import { isEqual } from 'lodash';

// -----------------------------------------------------------------------------
const { LABORATORY_CHECK } = ARKON.CS.ENCOUNTER.TYPE.CODES;

// -----------------------------------------------------------------------------
interface EncounterFormProps {
  patient: Patient;
  onEncounterCreated: (encounter: Encounter) => void;
  onBack?: () => void;
  showPatientInfo?: boolean;
  showAdditionalNotes?: boolean;
  submitButtonText?: string;
  loading?: boolean;
  onFormStateChange?: (hasChanges: boolean) => void;
  showDiscardButton?: boolean;
  encounter?: Encounter;
}

// -----------------------------------------------------------------------------
export const EncounterForm: FC<EncounterFormProps> = ({
  patient,
  onEncounterCreated,
  onBack,
  showPatientInfo = true,
  showAdditionalNotes = true,
  submitButtonText,
  loading: externalLoading,
  onFormStateChange,
  showDiscardButton = false,
  encounter,
}) => {
  const { t } = useTranslation(['encounters', 'patient-onboarding']);
  const { createEncounter, updateEncounter } = useEncounterOperations();

  const [encounterType, setEncounterType] = useState<
    CodeableConcept | undefined
  >();

  const [date, setDate] = useState<Date | null>(new Date());
  const [time, setTime] = useState<string>(dayjs().format('HH:mm'));
  const [attendingDoctor, setAttendingDoctor] = useState<
    Practitioner | undefined
  >();

  const [additionalDoctors, setAdditionalDoctors] = useState<Practitioner[]>(
    []
  );

  const [reasonMode, setReasonMode] = useState<ReasonMode>('text');
  const [reasonText, setReasonText] = useState<string>('');
  const [reasonCode, setReasonCode] = useState<CodeableConcept | undefined>();
  const [referringDoctors, setReferringDoctors] = useState<Practitioner[]>([]);

  const [additionalNotes, setAdditionalNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const [quickAddOpened, { open: openQuickAdd, close: closeQuickAdd }] =
    useDisclosure(false);

  // Check if encounter type is laboratory check - affects required fields
  const isLaboratoryCheck = useMemo(() => {
    return encounterType?.coding?.[0]?.code === LABORATORY_CHECK;
  }, [encounterType]);

  // Initialize form from encounter and capture baseline for change detection
  const { baselineRef } = useEncounterFormInitialization({
    encounter,
    currentState: {
      encounterType,
      date,
      time,
      attendingDoctor,
      additionalDoctors,
      reasonMode,
      reasonText,
      reasonCode,
      referringDoctors,
      additionalNotes,
    },
    setters: {
      setEncounterType,
      setDate,
      setTime,
      setAttendingDoctor,
      setAdditionalDoctors,
      setReasonMode,
      setReasonText,
      setReasonCode,
      setReferringDoctors,
      setAdditionalNotes,
    },
  });

  // Check for unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    const currentState = {
      encounterType,
      date,
      time,
      attendingDoctor,
      additionalDoctors,
      reasonMode,
      reasonText,
      reasonCode,
      referringDoctors,
      additionalNotes,
    };

    return !isEqual(currentState, baselineRef.current);
  }, [
    baselineRef,
    encounterType,
    date,
    time,
    attendingDoctor,
    additionalDoctors,
    reasonMode,
    reasonText,
    reasonCode,
    referringDoctors,
    additionalNotes,
  ]);

  // Notify parent of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(hasUnsavedChanges());
    }
  }, [hasUnsavedChanges, onFormStateChange]);

  // Handle discard changes
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setEncounterType(baselineRef.current.encounterType);
      setDate(baselineRef.current.date);
      setTime(baselineRef.current.time);
      setAttendingDoctor(baselineRef.current.attendingDoctor);
      setAdditionalDoctors(baselineRef.current.additionalDoctors);
      setReasonMode(baselineRef.current.reasonMode);
      setReasonText(baselineRef.current.reasonText);
      setReasonCode(baselineRef.current.reasonCode);
      setReferringDoctors(baselineRef.current.referringDoctors);
      setAdditionalNotes(baselineRef.current.additionalNotes);
    }
  };

  // ---------------------------------------------------------------------------
  const handleDoctorCreated = (doctor: Practitioner) => {
    setReferringDoctors([...referringDoctors, doctor]);
    closeQuickAdd();
  };

  // ---------------------------------------------------------------------------
  const validateForm = (): boolean => {
    if (!encounterType) {
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message: t('encounterTypeRequired', { ns: 'encounters' }),
      });

      return false;
    }

    // For laboratory checks: referring doctor required, attending doctor optional
    // For other types: attending doctor required, referring doctor optional
    if (isLaboratoryCheck && referringDoctors.length === 0) {
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message: t('referringDoctorRequired', { ns: 'encounters' }),
      });

      return false;
    } else if (!isLaboratoryCheck && !attendingDoctor) {
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message: t('attendingDoctorRequired', { ns: 'encounters' }),
      });

      return false;
    }

    if (!reasonText.trim() && reasonMode === 'text') {
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message: t('reasonForVisitRequired', { ns: 'encounters' }),
      });

      return false;
    }

    if (!reasonCode && reasonMode === 'icd10') {
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message: t('reasonCodeRequired', { ns: 'encounters' }),
      });

      return false;
    }

    if (!date || !time) {
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message: 'Date and time are required',
      });

      return false;
    }

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(time)) {
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message: t('invalidTime', { ns: 'encounters' }),
      });

      return false;
    }

    return true;
  };

  // ---------------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Type guards - validateForm() already ensures these are valid
    if (!encounterType || !date) {
      return;
    }

    setLoading(true);
    try {
      const formData: EncounterFormData = {
        encounterType,
        date,
        time,
        attendingDoctor,
        additionalDoctors,
        reasonMode,
        reasonText,
        reasonCode,
        referringDoctors,
        additionalNotes,
        showAdditionalNotes,
      };

      let resultEncounter: Encounter;

      if (encounter) {
        // Update existing encounter
        resultEncounter = await updateEncounter(patient, formData, encounter);
        notifications.show({
          ...BASE_SUCCESS_NOTIFICATION_PARAMS,
          title: t('success', { ns: 'encounters' }),
          message: t('edited', { ns: 'encounters' }),
        });
      } else {
        // Create new encounter
        resultEncounter = await createEncounter(patient, formData);
        notifications.show({
          ...BASE_SUCCESS_NOTIFICATION_PARAMS,
          title: t('success', { ns: 'encounters' }),
          message: t('created', { ns: 'encounters' }),
        });
      }

      onEncounterCreated(resultEncounter);
    } catch (error) {
      console.error('Error saving encounter:', error);
      notifications.show({
        ...BASE_ERROR_NOTIFICATION_PARAMS,
        message:
          error instanceof Error
            ? error.message
            : t('createError', { ns: 'encounters' }),
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  const isLoading = loading || externalLoading;

  // ---------------------------------------------------------------------------
  return (
    <Stack gap="lg">
      {/* Patient Info Card */}
      {showPatientInfo && (
        <Card withBorder p="sm" bg="gray.0">
          <Group gap="xs">
            <Text size="sm" fw={500}>
              {t('patient', { ns: 'encounters' })}:
            </Text>
            <Text size="sm">{formatHumanName(patient.name?.[0])}</Text>
          </Group>
        </Card>
      )}

      {/* Encounter Type */}
      <CodeableConceptInput
        key={
          encounterType
            ? `enc-type-${encounterType?.coding?.[0]?.code ?? 'val'}`
            : 'enc-type-empty'
        }
        path="Encounter.type"
        binding={ARKON.VS.ENCOUNTER_TYPE_EL}
        label={t('encounterType', { ns: 'encounters' })}
        name="type"
        maxValues={1}
        defaultValue={encounterType}
        onChange={(type) => setEncounterType(type)}
        required
      />

      {/* Date and Time */}
      <Grid>
        <Grid.Col span={6}>
          <DateInputWrapper
            name="date"
            label={t('date', { ns: 'encounters' })}
            value={date}
            onChange={setDate}
            leftSection={<IconCalendar size={16} />}
            required
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TimeInput
            label={t('time', { ns: 'encounters' })}
            value={time}
            onChange={(e) => setTime(e.target.value)}
            leftSection={<IconClock size={16} />}
            required
          />
        </Grid.Col>
      </Grid>

      {/* Attending Doctor - required for non-laboratory encounters */}
      <ResourceInput<Practitioner>
        key={
          attendingDoctor
            ? `att-doc-${attendingDoctor.id ?? 'val'}`
            : 'att-doc-empty'
        }
        resourceType="Practitioner"
        name="attendingDoctor"
        label={t('attendingDoctor', { ns: 'encounters' })}
        placeholder={t('selectAttendingDoctor', { ns: 'encounters' })}
        defaultValue={attendingDoctor}
        onChange={(value) =>
          setAttendingDoctor(value as Practitioner | undefined)
        }
        required={!isLaboratoryCheck}
      />

      {/* Additional Doctors */}
      <AdditionalDoctorsList
        doctors={additionalDoctors}
        onChange={setAdditionalDoctors}
        label={t('additionalDoctors', { ns: 'encounters' })}
      />

      {/* Reason for Visit with Text/ICD-10 Toggle */}
      <Group justify="space-between">
        <Box flex={1}>
          {reasonMode === 'text' ? (
            <TextInput
              label={t('reasonForVisit', { ns: 'encounters' })}
              placeholder={t('reasonForVisitPlaceholder', { ns: 'encounters' })}
              value={reasonText}
              onChange={(e) => setReasonText(e.currentTarget.value)}
              required
            />
          ) : (
            <CodeableConceptInput
              path="Condition.code.coding"
              binding={ARKON.VS.ICD_10_EL}
              name="reasonCode"
              label="ICD-10"
              defaultValue={reasonCode}
              onChange={setReasonCode}
              required
            />
          )}
        </Box>
        <Switch
          mt="md"
          checked={reasonMode === 'icd10'}
          onChange={(e) =>
            setReasonMode(e.currentTarget.checked ? 'icd10' : 'text')
          }
          label={t('useIcd10', { ns: 'encounters' })}
        />
      </Group>

      {/* Referring Doctors - required for laboratory encounters */}
      <div>
        <AdditionalDoctorsList
          doctors={referringDoctors}
          onChange={setReferringDoctors}
          label={t('referringDoctor', { ns: 'encounters' })}
          required={isLaboratoryCheck}
        />
        <Anchor size="sm" mt={4} onClick={openQuickAdd}>
          <Group gap={4}>
            <IconPlus size={14} />
            {t('addNewExternalDoctor', { ns: 'encounters' })}
          </Group>
        </Anchor>
      </div>

      {/* Additional Notes */}
      {showAdditionalNotes && (
        <Textarea
          label={t('additionalNotes', { ns: 'encounters' })}
          placeholder={t('additionalNotesPlaceholder', { ns: 'encounters' })}
          value={additionalNotes}
          onChange={(e) => setAdditionalNotes(e.currentTarget.value)}
          minRows={3}
        />
      )}

      {/* Actions */}
      <Group justify="space-between" mt="xl">
        {showDiscardButton && (
          <DiscardChangesButton
            onDiscard={handleDiscardChanges}
            disabled={!hasUnsavedChanges() || isLoading}
            loading={isLoading}
          />
        )}
        <Group ml={showDiscardButton ? 0 : onBack ? 0 : 'auto'}>
          {onBack && (
            <Button
              variant="outline"
              leftSection={<IconArrowLeft size={16} />}
              onClick={onBack}
              disabled={isLoading}
            >
              {t('buttons.back', { ns: 'encounters' })}
            </Button>
          )}
          <Button onClick={handleSubmit} loading={isLoading} size="md">
            {submitButtonText ||
              (encounter
                ? t('buttons.save', { ns: 'encounters' })
                : t('buttons.create', { ns: 'encounters' }))}
          </Button>
        </Group>
      </Group>

      {/* Quick Add Referring Doctor Modal */}
      <CreatePractitionerModal
        opened={quickAddOpened}
        onClose={closeQuickAdd}
        onPractitionerCreated={handleDoctorCreated}
      />
    </Stack>
  );
};
